import { AuthFetchWithResponse } from 'src/Types/api'
import { ApiResult } from 'src/Libs/api'
import { StatusCodes } from 'src/Libs/http-status-codes'
import { ConnectVehicle, FetchAuthUrlPayload, FetchAuthUrlResponse, UserVehicle } from '../models'
import { transformConnectVehiclesDtoToModel, transformUserVehiclesDtoToModel } from '../transformer'
import { ConnectVehicleDto, UserVehicleDto } from '../dto'
import { AuthFetchError } from '@e3dc-react/shell/Types/api'
import { handleFetchError } from 'src/Libs/api/utils'
import { isUndefined } from 'lodash'
import { isFlowTheme } from 'src/Libs/utils'

/**
 * Returns a list of user vehicles
 */
export const fetchUserVehicles = async (
  authFetch: AuthFetchWithResponse,
  installationId?: number
): Promise<ApiResult<UserVehicle[], AuthFetchError | Error>> => {
  try {
    if (isUndefined(installationId) || isFlowTheme) {
      return new ApiResult<UserVehicle[], Error | AuthFetchError>(null, true, new Error('no installation id was given or Flow theme is active'))
    }
    const response = await authFetch<UserVehicleDto[]>({
      path: `vehicles/installations/${installationId}/vehicles`,
      method: 'GET',
      endpoint: process.env.REACT_APP_VEHICLES_ENDPOINT,
    })

    if (response.status === StatusCodes.OK) {
      const userVehicles = transformUserVehiclesDtoToModel(response.data)
      return new ApiResult<UserVehicle[], Error | AuthFetchError>(userVehicles)
    }

    return new ApiResult<UserVehicle[], AuthFetchError | Error>(null, true)
  } catch (error) {
    return handleFetchError<UserVehicle[]>(error)
  }
}

/**
 * Returns a list of vehicles that can be connected after a user authorization
 * @param userId
 * @param vendorAccountId
 * @param authFetch
 */
export const fetchConnectVehicles = async (
  installationId: string,
  vendorAccountId: string,
  authFetch: AuthFetchWithResponse
): Promise<ApiResult<ConnectVehicle[], AuthFetchError | Error>> => {
  try {
    const response = await authFetch<ConnectVehicleDto[]>({
      path: `vehicles/installations/${installationId}/vendors/${vendorAccountId}`,
      method: 'GET',
      endpoint: process.env.REACT_APP_VEHICLES_ENDPOINT,
    })

    if (response.status === StatusCodes.OK) {
      const connectVehicle = transformConnectVehiclesDtoToModel(response.data)
      return new ApiResult<ConnectVehicle[], Error | AuthFetchError>(connectVehicle)
    }

    return new ApiResult<ConnectVehicle[], AuthFetchError | Error>(null, true)
  } catch (error) {
    return handleFetchError<ConnectVehicle[]>(error)
  }
}

/**
 * Returns an URL which is required to authenticate yourself via your previously selected vehicle manufacturer
 * This step is needed to get the users vehicle data via EHA API
 * @param userId should be obsolete when api is updated
 * @param vendor vendorID (TESLA, BMW, etc.)
 * @param vin Vehicle-Identification-Number
 * All those params are included in "payload"
 */
export const fetchAuthenticationUrl = async (
  payload: FetchAuthUrlPayload,
  installationId: number,
  authFetch: AuthFetchWithResponse
): Promise<ApiResult<string, AuthFetchError | Error>> => {
  try {
    const response = await authFetch<FetchAuthUrlResponse>({
      path: `vehicles/installations/${installationId}/connections`,
      method: 'POST',
      payload: payload,
      endpoint: process.env.REACT_APP_VEHICLES_ENDPOINT,
    })

    if (response.status === StatusCodes.OK) {
      return new ApiResult<string, Error | AuthFetchError>(response.data.url)
    }

    return new ApiResult<string, AuthFetchError | Error>(null, true)
  } catch (error) {
    return handleFetchError<string>(error)
  }
}

/**
 * Returns a list of vehicles that can be connected after a user authorization
 * @param userId
 * @param vendorAccountId
 * @param authFetch
 */
export const connectVehicles = async (
  vendorAccountId: string,
  externalVehicles: string[],
  installationId: number,
  authFetch: AuthFetchWithResponse
): Promise<ApiResult<ConnectVehicle[], AuthFetchError | Error>> => {
  try {
    const response = await authFetch<ConnectVehicleDto[]>({
      path: `vehicles/installations/${installationId}/vendors/${vendorAccountId}`,
      method: 'POST',
      endpoint: process.env.REACT_APP_VEHICLES_ENDPOINT,
      payload: { externalVehicles },
    })

    if (response.status === StatusCodes.OK) {
      const connectVehicle = transformConnectVehiclesDtoToModel(response.data)
      return new ApiResult<ConnectVehicle[], Error | AuthFetchError>(connectVehicle)
    }

    return new ApiResult<ConnectVehicle[], AuthFetchError | Error>(null, true)
  } catch (error) {
    return handleFetchError<ConnectVehicle[]>(error)
  }
}

/**
 * Sends a request to disconnect a vehicle from an installation
 */
export const disconnectVehicleFromInstallation = async (
  vehicleId: number,
  installationId: number,
  authFetch: AuthFetchWithResponse
): Promise<ApiResult<boolean, AuthFetchError | Error>> => {
  try {
    const response = await authFetch<boolean>({
      path: `vehicles/installations/${installationId}/vehicles/${vehicleId}`,
      method: 'DELETE',
      endpoint: process.env.REACT_APP_VEHICLES_ENDPOINT,
    })

    if (response.status === StatusCodes.NO_CONTENT) {
      return new ApiResult<boolean, Error | AuthFetchError>(true)
    }

    return new ApiResult<boolean, AuthFetchError | Error>(false, true)
  } catch (error) {
    return handleFetchError<boolean>(error)
  }
}
